// Start (Canvas R3-T1-Start): Gruss, "Heute kochen wir" mit Heute-Karte, "Warum das gut tut",
// Vorschau auf Meine Woche mit "Heute kochen" je Gericht.
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { WochenEintrag } from '../api/client';
import { useIch } from '../api/abfragen';
import { useWoche } from '../api/woche';
import { ausIso, langesDatum, tagImSatz, tagName } from '../lib/datum';
import { Knopf, KnopfLink, LeerZustand, ProfilKnopf, RezeptBild, StartKopf, WarumGutKarte } from '../ui';
import { eintragArt, GerichtWahl, HeuteKarte, metaText, rezeptLink, useWochenAktionen, wocheUnterzeile } from './WocheTeile';
import seite from './seiten.module.css';
import s from './woche.module.css';

/** "Warum das gut tut" ohne Gericht fuer heute (QA Runde 2 N21): das naechste eingeplante Gericht mit Text,
 * sonst eines aus der Auswahl, taeglich ein anderes (stabil ueber den Tag). */
function rezeptDesTages(liste: readonly WochenEintrag[], heute: string): WochenEintrag | undefined {
  const mitText = liste.filter((e) => e.rezept.warum_gut);
  const naechstes = mitText.filter((e) => e.datum && e.datum >= heute).sort((a, b) => (a.datum ?? '').localeCompare(b.datum ?? ''))[0];
  if (naechstes) return naechstes;
  if (mitText.length === 0) return undefined;
  const tag = Math.floor(ausIso(heute).getTime() / 86_400_000);
  return mitText[tag % mitText.length];
}

export function Start() {
  const ich = useIch();
  const navigate = useNavigate();
  const woche = useWoche();
  const [wahl, setWahl] = useState(false);
  const name = ich.data?.anzeigename ?? '';
  const heute = woche.data?.heute ?? '';
  const aktionen = useWochenAktionen(heute);
  const eintraege = woche.data?.eintraege ?? [];

  const heuteListe = eintraege.filter((e) => e.datum === heute);
  const hero = heuteListe.find((e) => !e.gekocht_am) ?? heuteListe[0];
  const offen = useMemo(
    () => eintraege.filter((e) => e.id !== hero?.id && ['auswahl', 'geplant'].includes(eintragArt(e, heute))),
    [eintraege, hero?.id, heute],
  );
  const pool = offen.filter((e) => eintragArt(e, heute) === 'auswahl');
  const warum = hero?.rezept.warum_gut ? hero : rezeptDesTages(offen, heute);

  return (
    <>
      <StartKopf rechts={<ProfilKnopf name={name || '?'} onClick={() => navigate('/profil')} />} />
      <section className={seite.gruss}>
        <h1 className={seite.display}>Hallo {name}</h1>
        <p className={seite.datum}>{heute ? langesDatum(heute) : ' '}</p>
      </section>

      {woche.data && eintraege.length === 0 ? (
        <>
          <p className={seite.eyebrow}>
            <span aria-hidden="true" className={seite.eyebrowPunkt} />
            Heute kochen wir
          </p>
          <LeerZustand
            titelId="start-leer"
            bild="/illustrationen/leer-woche.webp"
            titel="Heute steht noch nichts an"
            text="Sobald eure Woche gefüllt ist, siehst du hier, was heute auf den Tisch kommt."
            aktionen={
              <>
                <KnopfLink an="/woche" icon="woche">
                  Zu Meine Woche
                </KnopfLink>
                <KnopfLink an="/entdecken" art="kontur" icon="entdecken">
                  Rezepte entdecken
                </KnopfLink>
              </>
            }
          />
        </>
      ) : woche.data ? (
        <>
          <section aria-labelledby="start-heute">
            <p className={seite.eyebrow} id="start-heute">
              <span aria-hidden="true" className={seite.eyebrowPunkt} />
              {hero?.gekocht_am ? 'Heute gekocht' : 'Heute kochen wir'}
            </p>
            {hero ? (
              <>
                <HeuteKarte
                  eintrag={hero}
                  tauschenLabel="Anderes Gericht für heute wählen"
                  onTauschen={hero.gekocht_am ? undefined : () => setWahl(true)}
                  jetztKochen={!hero.gekocht_am}
                />
                {heuteListe.length > 1 && (
                  <p className={s.heroWeitere}>
                    Außerdem heute:{' '}
                    {heuteListe
                      .filter((e) => e.id !== hero.id)
                      .map((e, i) => (
                        <span key={e.id}>
                          {i > 0 && ', '}
                          <Link to={rezeptLink(e.rezept)}>{e.rezept.name}</Link>
                        </span>
                      ))}
                  </p>
                )}
              </>
            ) : (
              <div className={s.tagLeer}>
                <p>Für heute ist noch nichts geplant. Such dir etwas aus eurer Woche aus.</p>
                <Knopf icon="kochen" onClick={() => setWahl(true)}>
                  Gericht für heute wählen
                </Knopf>
              </div>
            )}
          </section>

          {warum && (
            <WarumGutKarte
              id="start-warum"
              abstand="start"
              text={warum.rezept.warum_gut ?? ''}
              fuss={
                warum !== hero && (
                  <p className={s.warumQuelle}>
                    Zu <Link to={rezeptLink(warum.rezept)}>{warum.rezept.name}</Link>
                    {warum.datum ? `, geplant für ${tagImSatz(warum.datum, heute)}` : ', aus eurer Auswahl'}
                  </p>
                )
              }
            />
          )}

          {offen.length > 0 && (
            <section aria-labelledby="start-woche">
              <div className={s.wocheKopf}>
                <div>
                  <h2 id="start-woche" className={s.wocheTitel}>
                    Meine Woche
                  </h2>
                  <p className={s.wocheUnter}>{wocheUnterzeile(eintraege, heute)}</p>
                </div>
                <Link to="/woche" className={s.alleLink}>
                  Alle
                </Link>
              </div>
              <div className={s.vorschauRaster}>
                {offen.slice(0, 8).map((e) => (
                  <article key={e.id} className={s.vorschauKarte}>
                    <Link to={rezeptLink(e.rezept)} className={s.vorschauBild}>
                      <RezeptBild bild={e.rezept.bild} kategorie={e.rezept.kategorie} bildStatus={e.rezept.bild_status} sizes="(max-width: 640px) 50vw, 290px" />
                      <span className={s.vorschauLabel}>
                        <span className={s.vorschauName}>{e.rezept.name}</span>
                      </span>
                    </Link>
                    <p className={s.vorschauMeta}>{metaText(e.rezept)}</p>
                    {e.datum ? (
                      <span className={`${s.pille} ${s.vorschauPille}`}>Geplant · {tagName(e.datum, heute)}</span>
                    ) : (
                      <button type="button" className={`${s.konturKlein} ${s.vorschauKnopf}`} onClick={() => aktionen.heuteKochen(e)}>
                        Heute kochen
                      </button>
                    )}
                  </article>
                ))}
              </div>
            </section>
          )}
        </>
      ) : null}

      <GerichtWahl
        offen={wahl}
        titel={hero ? 'Anderes Gericht für heute' : 'Was gibt es heute?'}
        unterzeile={hero ? `${hero.rezept.name} geht zurück zur Auswahl.` : undefined}
        pool={pool}
        onPool={(p) => {
          setWahl(false);
          aktionen.tauschen(p, heute, hero);
        }}
        onRezept={(r) => {
          setWahl(false);
          aktionen.hinzufuegen(r, heute);
          if (hero && !hero.gekocht_am) aktionen.zurueckInAuswahl(hero);
        }}
        onSchliessen={() => setWahl(false)}
      />
    </>
  );
}
